import React, { FC, useMemo } from 'react'
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames'
import { Field, FormikErrors, FormikTouched } from 'formik'
import nestedProperty from 'nested-property'

import Box from '@mui/material/Box'
import Grid, { GridSpacing, GridSize } from '@mui/material/Grid'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import FormHelperText from '@mui/material/FormHelperText'
import utils from './utils'
import Errors from './Errors'

import {
  FormSchema,
  FormSchemaField,
  FormHandlers,
} from '../../../types/frontend/form'

import {
  ComponentName,
} from './fields'

const useStyles = makeStyles(theme => ({
}))

function isRowFieldArray(array: FormSchemaField[] | FormSchemaField): array is FormSchemaField[] {
  return array.constructor === Array
}

interface FormWrapperItemProps {
  item: FormSchemaField,
  values: any,
  readonly?: boolean,
  errors: FormikErrors<any>,
  touched: FormikTouched<any>,
  handlers?: FormHandlers,
}

const FormWrapperItem: FC<React.PropsWithChildren<FormWrapperItemProps>> = ({
  item,
  values,
  readonly,
  errors,
  touched,
  handlers,  
}) => {

  const fieldError = nestedProperty.get(errors, item.id)
  const fieldTouched = nestedProperty.get(touched, item.id)

  return (
    <Field
      name={ item.id }
      component={ utils.getComponent(item.component as ComponentName) }
      item={ item }
      readonly={ readonly || item.readonly ? true : false }
      values={ values }
      error={ fieldError }
      touched={ fieldTouched }
      handlers={ handlers }
    />
  )
}

interface FormWrapperRowProps {
  rowKey: number,
  row: FormSchemaField[] | FormSchemaField,
  values: any,
  readonly?: boolean,
  errors: FormikErrors<any>,
  touched: FormikTouched<any>,
  handlers?: FormHandlers,
}

const FormWrapperRow: FC<React.PropsWithChildren<FormWrapperRowProps>> = ({
  rowKey,
  row,
  values,
  readonly,
  errors,
  touched,
  handlers,
}) => {
  const classes = useStyles()
  if (isRowFieldArray(row)) {
    // TODO: find out a better way to do this
    // <Grid sm={ colSize} /> complains
    // at just "number"
    const colSize = Math.floor(12 / row.length) as GridSize
    return (
      <>
        {
          row
            .filter(item => {
              if(!handlers || !handlers.hidden) return true
              return handlers.hidden({
                name: item.id,
                values,
              }) ? false : true
            })
            .map((item, i) => (
              <Grid item xs={ 12 } sm={ colSize } key={ rowKey + '-' + i }>
                <FormWrapperItem
                  item={ item }
                  values={ values }
                  readonly={ readonly }
                  errors={ errors }
                  touched={ touched }
                  handlers={ handlers }
                />
              </Grid>
            ))
        }
      </>
    )
  }
  else if(row.divider) {
    return (
      <Grid item xs={ 12 } key={ rowKey }>
        {
          !row.title ? (
            <Divider
              sx={{
                marginTop: '20px',
                marginBottom: '20px',
              }}
            />
          ) : (
            <Typography
              variant='subtitle1'
              style={{
                fontWeight: 'bold',
                marginTop: '10px',
                marginBottom: '10px',
              }}
            >
              { row.title }
            </Typography>
          )
        }
      </Grid>
    )
  }
  else {
    if(handlers && handlers.hidden && handlers.hidden({
      name: row.id,
      values,
    })) {
      return null
    }
    
    return (
      <Grid item xs={12} key={ rowKey }>
        <FormWrapperItem
          item={ row }
          values={ values }
          readonly={ readonly }
          errors={ errors }
          touched={ touched }          
          handlers={ handlers }
        />
      </Grid>
    )
  }
}

interface FormRenderProps {
  schema: FormSchema,
  handlers?: FormHandlers,
  readonly?: boolean,
  error?: string,
  values: any,
  spacing?: GridSpacing,
  errors: FormikErrors<any>,
  touched: FormikTouched<any>,
  showErrors: boolean,
  fullHeight?: boolean,
  isValid: boolean,
  hideBottomError?: boolean,
}

const FormRender: FC<React.PropsWithChildren<FormRenderProps>> = ({
  schema,
  handlers,
  readonly,
  error,
  values,
  spacing = 2,
  errors,
  touched,
  showErrors,
  fullHeight,
  hideBottomError = false,  
}) => {

  const classes = useStyles()

  return (
    <Box
      sx={{
        p: 1,
      }}
    >
      <Grid
        container
        spacing={ spacing }
        sx={{
          height: '100%',
        }}
      >
        {
          schema.map((row, i) => {
            return (
              <FormWrapperRow
                key={ i }
                rowKey={ i }
                readonly={ readonly }
                row={ row }
                values={ values }
                errors={ errors }
                touched={ touched }
                handlers={ handlers }
              />
            )
          })
        }
        {
          error && typeof(error) === 'string' && (
            <Box
              sx={{
                p: 1
              }}
            >
              <FormHelperText
                error={ true }
              >
                { error }
              </FormHelperText>
            </Box>
          )
        }
      </Grid>
      {
        !hideBottomError && (
          <Errors
            showErrors={ showErrors }
            errors={ errors }
          />
        )
      }
    </Box>
  )
}

export default FormRender